import { policyHeaders } from "@entities/policies/lib/constants";
import { useSearchPermissionGroups } from "@entities/policies/lib/hooks/useSearchPermissionGroups";
import { PermissionGroup as PermissionGroupType } from "@shared/lib/types/permissions";
import { Button, Col, Row, Typography } from "@shared/ui";
import { DisabledPopover } from "@shared/ui/DisabledPopover";
import { Drawer, Space } from "antd";
import { ArrowLeft, Pen, Plus, Shield } from "lucide-react";
import { useState } from "react";
import { PolicyForm } from "../PolicyForm";
import styles from "./styles.module.scss";

const { Title, Text, Paragraph } = Typography;

interface ManagePoliciesDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  brandOrganisationId: string;
}

export default function ManagePoliciesDrawer({
  isOpen,
  onClose,
  brandOrganisationId,
}: ManagePoliciesDrawerProps) {
  const [view, setView] = useState<"list" | "create" | "edit">("list");
  const [editingPolicy, setEditingPolicy] =
    useState<PermissionGroupType | null>(null);

  const [highlightedPolicyId, setHighlightedPolicyId] = useState<string>("");

  const { permissionGroups, queryKey } = useSearchPermissionGroups({
    filterBy: {
      brandOrganisationId: brandOrganisationId || "",
    },
    limit: 100,
    pageNo: 1,
  });

  const policies = permissionGroups?.data?.data || [];

  const handleCreateClick = () => {
    setEditingPolicy(null);
    setView("create");
  };

  const handleEditClick = (policy: PermissionGroupType) => {
    setEditingPolicy(policy);
    setView("edit");
  };

  const currentHeader =
    view === "create"
      ? policyHeaders[1]
      : view === "edit"
        ? policyHeaders[2]
        : policyHeaders[0];

  const handleBack = () => {
    setView("list");
  };

  return (
    <Drawer
      open={isOpen}
      onClose={() => {
        setView("list");
        onClose();
      }}
      width={600}
      title={
        <Row align="middle" gutter={5}>
          {currentHeader.operation === "back" && (
            <Col>
              <Button
                type="text"
                shape="circle"
                size="small"
                icon={<ArrowLeft size={20} />}
                onClick={handleBack}
              />
            </Col>
          )}
          <Col>
            <Title level={4} style={{ margin: 0 }}>
              {currentHeader.title}
            </Title>
            <Text type="secondary" className={styles.drawerSubtitle}>
              {currentHeader.subtitle}
            </Text>
          </Col>
        </Row>
      }
      footer={null}
      destroyOnClose
    >
      {view === "list" ? (
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          <Button
            className={styles.createButton}
            type="dashed"
            onClick={handleCreateClick}
            icon={<Plus size={14} />}
          >
            Create New Policy
          </Button>
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            {policies.map((policy) => (
              <div
                key={policy._id}
                className={styles.policyCard}
                onMouseEnter={() => setHighlightedPolicyId(policy._id)}
                onMouseLeave={() => setHighlightedPolicyId("")}
              >
                <Row gutter={10} align="top">
                  <Col flex="none">
                    <div className={styles.policyIcon}>
                      <Shield size={20} />
                    </div>
                  </Col>
                  <Col flex="auto" className={styles.policyInfo}>
                    <Title
                      level={4}
                      style={{ margin: 0, fontSize: "15px !important" }}
                    >
                      {policy.name}
                    </Title>
                    <span className={styles.policyDesc}>
                      {policy.description}
                    </span>
                    <div className={styles.policyMeta}>
                      <span>
                        {policy.permissions.includes("all")
                          ? "Full Access"
                          : `${policy.permissions.length} Permissions`}
                      </span>
                      <div className={styles.dot} />
                      <span>
                        {policy.countOfAttachedToBrandUsers || 0} users assigned
                      </span>
                    </div>
                  </Col>
                  <Col flex="none">
                    {highlightedPolicyId === policy._id && (
                      <DisabledPopover
                        isDisabled={policy.isSystemGenerated || false}
                        content={
                          policy.isSystemGenerated
                            ? "System generated policy"
                            : undefined
                        }
                      >
                        <Button
                          type="text"
                          disabled={policy.isSystemGenerated || false}
                          size="small"
                          icon={<Pen size={16} />}
                          onClick={() => handleEditClick(policy)}
                        />
                      </DisabledPopover>
                    )}
                  </Col>
                </Row>
              </div>
            ))}
          </Space>
        </Space>
      ) : (
        <PolicyForm
          brandOrganisationId={brandOrganisationId}
          queryKey={queryKey}
          isEdit={view === "edit"}
          policy={editingPolicy || undefined}
          onCancel={() => setView("list")}
        />
      )}
    </Drawer>
  );
}
